import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { recruitingAPI } from "../services/recruitingApi";

interface LinkedInPostGeneratorProps {
  jobId: string | number;
}

interface FormData {
  companyName: string;
  benefits: string;
  salaryRange: string;
  contactEmail: string;
  contactPhone: string;
  additionalNotes: string;
  includeHashtags: boolean;
  tone: "professional" | "friendly" | "casual";
  [key: string]: unknown;
}

interface GeneratedPost {
  formattedPost: string;
  hashtags?: string[];
}

interface ApiResponse {
  data: GeneratedPost;
}

const LinkedInPostGenerator: React.FC<LinkedInPostGeneratorProps> = ({
  jobId,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const [formData, setFormData] = useState<FormData>({
    companyName: "",
    benefits: "",
    salaryRange: "",
    contactEmail: "",
    contactPhone: "",
    additionalNotes: "",
    includeHashtags: true,
    tone: "professional",
  });
  const [generatedPost, setGeneratedPost] = useState<GeneratedPost | null>(
    null,
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const generateMutation = useMutation<GeneratedPost, Error, FormData>(
    async (options) => {
      const result = (await recruitingAPI.generateLinkedInPost(
        jobId,
        options,
      )) as ApiResponse;
      return result.data;
    },
    {
      onSuccess: (result) => {
        setGeneratedPost(result);
        setIsGenerating(false);
        setError(null);
      },
      onError: (err) => {
        setIsGenerating(false);
        setError(err.message || t("recruiting:linkedIn.failedToGenerate"));
      },
    },
  );

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const target = e.target;
    const name = target.name;
    const value =
      target.type === "checkbox"
        ? (target as HTMLInputElement).checked
        : target.value;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGenerate = () => {
    setIsGenerating(true);
    setError(null);
    generateMutation.mutate(formData);
  };

  const handleCopy = () => {
    if (generatedPost?.formattedPost) {
      navigator.clipboard.writeText(generatedPost.formattedPost);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleEdit = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (generatedPost) {
      setGeneratedPost({
        ...generatedPost,
        formattedPost: e.target.value,
      });
    }
  };

  return (
    <div className="linkedin-post-generator">
      <div className="generator-header">
        <h3>{t("recruiting:linkedIn.title")}</h3>
      </div>

      {!generatedPost && (
        <div className="generator-form">
          <div className="form-section">
            <h4>{t("recruiting:linkedIn.postDetails")}</h4>

            <div className="form-group">
              <label htmlFor="companyName">
                {t("recruiting:linkedIn.companyName")}
              </label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                placeholder="e.g., TechCorp Inc."
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="benefits">
                {t("recruiting:linkedIn.benefits")}
              </label>
              <textarea
                id="benefits"
                name="benefits"
                value={formData.benefits}
                onChange={handleInputChange}
                placeholder="e.g., Health insurance, Remote work, Professional development..."
                rows={4}
              />
            </div>

            <div className="form-group">
              <label htmlFor="salaryRange">
                {t("recruiting:linkedIn.salaryRange")}
              </label>
              <input
                type="text"
                id="salaryRange"
                name="salaryRange"
                value={formData.salaryRange}
                onChange={handleInputChange}
                placeholder="e.g., $80,000 - $120,000"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="contactEmail">
                  {t("recruiting:linkedIn.contactEmail")}
                </label>
                <input
                  type="email"
                  id="contactEmail"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  placeholder="recruiting@company.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="contactPhone">
                  {t("recruiting:linkedIn.contactPhone")}
                </label>
                <input
                  type="tel"
                  id="contactPhone"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="tone">{t("recruiting:linkedIn.tone")}</label>
              <select
                id="tone"
                name="tone"
                value={formData.tone}
                onChange={handleInputChange}
              >
                <option value="professional">
                  {t("recruiting:linkedIn.tones.professional")}
                </option>
                <option value="friendly">
                  {t("recruiting:linkedIn.tones.friendly")}
                </option>
                <option value="casual">
                  {t("recruiting:linkedIn.tones.casual")}
                </option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="additionalNotes">
                {t("recruiting:linkedIn.additionalNotes")}
              </label>
              <textarea
                id="additionalNotes"
                name="additionalNotes"
                value={formData.additionalNotes}
                onChange={handleInputChange}
                placeholder="Any additional information or requirements..."
                rows={3}
              />
            </div>

            <div className="form-group checkbox-group">
              <label htmlFor="includeHashtags">
                <input
                  id="includeHashtags"
                  type="checkbox"
                  name="includeHashtags"
                  checked={formData.includeHashtags}
                  onChange={handleInputChange}
                />{" "}
                {t("recruiting:linkedIn.includeHashtags")}
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button
              className="btn-primary"
              onClick={handleGenerate}
              disabled={isGenerating || !formData.companyName}
            >
              {isGenerating
                ? t("recruiting:linkedIn.generating")
                : t("recruiting:linkedIn.generatePost")}
            </button>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {generatedPost && (
        <div className="generated-post">
          <div className="post-preview">
            <h4>{t("recruiting:linkedIn.generatedPost")}</h4>
            <textarea
              className="post-content"
              value={generatedPost.formattedPost}
              onChange={handleEdit}
              rows={20}
            />
          </div>

          <div className="post-actions">
            <button className="btn-primary" onClick={handleCopy}>
              {copied
                ? t("recruiting:linkedIn.copied")
                : t("recruiting:linkedIn.copyToClipboard")}
            </button>
            <button
              className="btn-secondary"
              onClick={() => setGeneratedPost(null)}
            >
              {t("recruiting:linkedIn.generateNewPost")}
            </button>
          </div>

          {generatedPost.hashtags && generatedPost.hashtags.length > 0 && (
            <div className="post-hashtags">
              <strong>{t("recruiting:linkedIn.hashtags")}</strong>
              <div className="hashtags-list">
                {generatedPost.hashtags.map((tag) => (
                  <span key={`hashtag-${tag}`} className="hashtag">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LinkedInPostGenerator;
